import { GlobalTimeRecordsQuery } from 'generated/resources/graphql'
import moment from 'moment'

type GlobalTimeRecord = GlobalTimeRecordsQuery['projectManagement']['globalTimeRecords'][number]

export interface CustomGlobalTimeRecord {
  favouriteProjectId: string | undefined
  projectId: string | undefined
  projectName: string | undefined
  projectStatus: string | undefined
  projectWorkerId: string | undefined
  projectAssigned: boolean | undefined
  subprojectName: string | undefined
  subprojectId?: string | undefined
  imputedMinutesList: {
    date: string
    imputedMinutes: number
    comment: string | null | undefined
    openClockInMinutes: number | null
    isFlexible?: boolean
    flexibleId?: string
    recordId?: string
  }[]
}

export const deleteRepeatedProjects = (projects: CustomGlobalTimeRecord[]) => {
  return projects.filter(
    (project, index) =>
      index ===
      projects.findIndex(
        (other) =>
          project.projectWorkerId === other.projectWorkerId &&
          project.subprojectId === other.subprojectId
      )
  )
}

const generateCommentFor = (
  existingComment: string | null,
  newComment: string | null
): string | null => {
  if (newComment === null) {
    return existingComment
  } else {
    if (existingComment === null) {
      return newComment
    } else {
      return existingComment.concat('\n', newComment)
    }
  }
}

const getInitialProjects = (
  filterValues: string[],
  favouriteProjects: CustomGlobalTimeRecord[],
  lastWeekProjects: CustomGlobalTimeRecord[]
) => {
  const hasFavouriteProjectsSelected = filterValues.includes('favourite_projects')
  const hasLastWeekProjectsSelected = filterValues.includes('last_week_projects')

  if (hasFavouriteProjectsSelected && hasLastWeekProjectsSelected) {
    return deleteRepeatedProjects([...favouriteProjects, ...lastWeekProjects])
  }
  if (hasFavouriteProjectsSelected) {
    return favouriteProjects
  }

  return lastWeekProjects
}

const openClockInDifference = (startTime: string | null | undefined) => {
  if (!startTime) {
    return null
  }

  // The clock-in date is current date but with UTC time, so we need to create a current date with UTC
  const startDate = moment(startTime)
  const currentDate = moment.tz(moment().format('YYYY-MM-DDTHH:mm:ss'), 'UTC')

  return currentDate.diff(startDate, 'minutes')
}

export const getWeeklyImputedMinutesList = (
  date: string,
  imputedMinutes: number,
  comment: string | null | undefined,
  openClockInMinutes: number | null,
  isFlexible?: boolean,
  customId?: string
) => {
  const imputedMinutesList = []
  for (let i = 0; i < 7; i++) {
    if (i === moment(date).weekday()) {
      imputedMinutesList.push({
        date,
        imputedMinutes,
        comment,
        openClockInMinutes,
        isFlexible,
        flexibleId: isFlexible ? customId : undefined,
        recordId: isFlexible ? undefined : customId,
      })
    } else {
      imputedMinutesList.push({
        date: moment(date).startOf('week').add(i, 'day').format('YYYY-MM-DD'),
        imputedMinutes: 0,
        comment: null,
        openClockInMinutes: null,
        isFlexible: undefined,
        flexibleId: undefined,
        recordId: undefined,
      })
    }
  }
  return imputedMinutesList
}

export const getCustomGlobalTimeRecordsForMyProjectsTable = (
  globalTimeRecords: GlobalTimeRecord[],
  favouriteProjects: CustomGlobalTimeRecord[],
  lastWeekProjects: CustomGlobalTimeRecord[],
  filterValues: string[]
) => {
  const myProjects: CustomGlobalTimeRecord[] = getInitialProjects(
    filterValues,
    favouriteProjects,
    lastWeekProjects
  )
  if (!globalTimeRecords) return myProjects
  globalTimeRecords.forEach((item) => {
    const existingProjectIndex = myProjects.findIndex(
      (project) =>
        project.projectWorkerId === item.projectWorker?.id &&
        project.subprojectId == item.subproject?.id
    )

    const favouriteProjectIndex = favouriteProjects.findIndex(
      (fp) =>
        fp.projectWorkerId === item.projectWorker?.id && fp.subprojectId == item.subproject?.id
    )
    // The project+subproject combo is not in the array yet
    if (existingProjectIndex === -1) {
      myProjects.push({
        favouriteProjectId: favouriteProjects[favouriteProjectIndex]?.favouriteProjectId,
        projectId: item.projectWorker?.imputableProject?.id,
        projectName: item.projectWorker?.imputableProject?.name,
        projectStatus: item.projectWorker?.imputableProject?.status,
        projectAssigned: item.projectWorker?.assigned,
        projectWorkerId: item.projectWorker?.id,
        subprojectName: item.subproject?.name || '',
        subprojectId: item.subproject?.id || undefined,
        imputedMinutesList: getWeeklyImputedMinutesList(
          item.date,
          item.imputedMinutes,
          item.comment,
          openClockInDifference(item.openClockIn),
          item.isFlexible,
          item.customId
        ),
      })
    } else {
      // The project+subproject combo is already in the array
      const imputedMinutesToUpdate =
        (myProjects[existingProjectIndex]?.imputedMinutesList[moment(item.date).weekday()]
          ?.imputedMinutes || 0) + item.imputedMinutes

      const openClockInToUpdate =
        myProjects[existingProjectIndex]?.imputedMinutesList[moment(item.date).weekday()]
          ?.openClockInMinutes || openClockInDifference(item.openClockIn)

      const commentToUpdate = generateCommentFor(
        myProjects[existingProjectIndex]?.imputedMinutesList[moment(item.date).weekday()]
          ?.comment || null,
        item.comment || null
      )

      const flexibleId = item.isFlexible
        ? item.customId
        : myProjects[existingProjectIndex]?.imputedMinutesList[moment(item.date).weekday()]
            ?.flexibleId

      const recordId = !item.isFlexible
        ? item.customId
        : myProjects[existingProjectIndex]?.imputedMinutesList[moment(item.date).weekday()]
            ?.recordId

      myProjects[existingProjectIndex]?.imputedMinutesList.splice(moment(item.date).weekday(), 1, {
        date: item.date,
        imputedMinutes: imputedMinutesToUpdate,
        comment: commentToUpdate,
        openClockInMinutes: openClockInToUpdate,
        isFlexible: item.isFlexible,
        flexibleId: flexibleId,
        recordId: recordId,
      })
    }
  })

  return myProjects
}
